import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  getAllCalendarEvents,
  addCalendarEvent as addCalendarEventService,
  updateCalendarEvent as updateCalendarEventService,
  deleteCalendarEvent as deleteCalendarEventService
} from '../firebase/calendarService';

const CalendarEventsContext = createContext(null);

/**
 * Shared calendar events state used by both the Calendar and Inspections pages.
 * Any mutation (add / update / delete) triggers a single re-fetch so every
 * consumer (calendar grid, inspections "Booked visit" column, upcoming timeline)
 * updates immediately without manual refresh.
 */
export const CalendarEventsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const refreshEvents = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const data = await getAllCalendarEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[CalendarEvents] refresh failed:', err);
    }
  }, [currentUser?.uid]);

  // Initial load
  useEffect(() => {
    if (!currentUser?.uid) return;
    setEventsLoading(true);
    refreshEvents().finally(() => setEventsLoading(false));
  }, [currentUser?.uid, refreshEvents]);

  // Refresh automatically whenever the browser tab becomes visible again
  // (handles navigating back from an external page or switching tabs)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshEvents();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refreshEvents]);

  const addEvent = useCallback(async (eventData) => {
    const id = await addCalendarEventService(eventData);
    await refreshEvents();
    return id;
  }, [refreshEvents]);

  const updateEvent = useCallback(async (eventId, eventData) => {
    await updateCalendarEventService(eventId, eventData);
    await refreshEvents();
  }, [refreshEvents]);

  const deleteEvent = useCallback(async (eventId) => {
    const result = await deleteCalendarEventService(eventId);
    await refreshEvents();
    return result;
  }, [refreshEvents]);

  return (
    <CalendarEventsContext.Provider
      value={{ events, eventsLoading, refreshEvents, addEvent, updateEvent, deleteEvent }}
    >
      {children}
    </CalendarEventsContext.Provider>
  );
};

export const useCalendarEvents = () => {
  const ctx = useContext(CalendarEventsContext);
  if (!ctx) throw new Error('useCalendarEvents must be used inside <CalendarEventsProvider>');
  return ctx;
};
